// Ordenamiento de las líneas de una nota de cobro.
//
// Vive aparte de la vista por dos motivos: se puede testear sin montar React, y las
// trampas que resuelve no son evidentes leyendo el JSX (el número de seguimiento es un
// string, y lo que se ve en pantalla está formateado y localizado).

// Peso de cada categoría de sobredimensionado, para poder ordenar la columna "Tarifa".
const SIZE_RANK = { XL: 1, '2XL': 2, '3XL': 3 };

// Claves de orden por columna. Devuelven SIEMPRE el valor crudo: ordenar por lo que se ve
// en pantalla (formatCurrency devuelve "$1.234", formatDate devuelve "04 ago 2026") daría
// un orden alfabético sin ningún sentido.
export const SORT_ACCESSORS = {
  // followingNumber es string en el schema, no número: se compara con `numeric` abajo.
  track: (li) => li.followingNumber ?? '',
  description: (li) => li.description ?? '',
  date: (li) => (li.deliveredAt ? new Date(li.deliveredAt).getTime() : 0),
  // "Tarifa" no es un valor sino la composición de cuatro marcadores, así que no tiene
  // orden natural. Convención adoptada: pesa primero el sobredimensionado (y dentro de
  // él, la categoría), después devuelto > manual > comuna. De mayor a menor deja arriba
  // las líneas excepcionales, que son las que se auditan.
  rate: (li) =>
    (SIZE_RANK[li.sizeCategory] ?? 0) * 8 +
    (li.returned ? 4 : 0) +
    (li.manualPriced ? 2 : 0) +
    (li.zonePriced ? 1 : 0),
  amount: (li) => li.totalAmount ?? 0,
};

// `numeric` hace que "2" < "10" y "Entrega #2" < "Entrega #10" en vez del orden
// lexicográfico, que es el que rompería tanto la columna # como la de descripción
// (derivada del mismo número).
export const compareValues = (a, b) =>
  typeof a === 'number' && typeof b === 'number'
    ? a - b
    : String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });

// Devuelve una copia ordenada. `sort` es { key, dir }; con key null se respeta el orden
// original de la nota (el mismo del PDF emitido).
export const sortLineItems = (items, sort) => {
  if (!sort?.key) return items;
  const accessor = SORT_ACCESSORS[sort.key];
  if (!accessor) return items;
  const factor = sort.dir === 'asc' ? 1 : -1;
  // slice() antes de sort(): sort muta in-place y este array puede ser el del store.
  return items.slice().sort((a, b) => factor * compareValues(accessor(a), accessor(b)));
};

// Primer click en una columna: de mayor a menor. Segundo: de menor a mayor. Tercero:
// vuelve al orden original.
export const nextSortState = (current, key) =>
  current.key !== key
    ? { key, dir: 'desc' }
    : current.dir === 'desc'
      ? { key, dir: 'asc' }
      : { key: null, dir: null };
